import PropTypes from 'prop-types';
import { useMemo, useLayoutEffect } from 'react';
import { motion } from 'motion/react';
import Link from 'next/link';
import Head from 'next/head';
import Image from 'next/image';
import { SEO } from './SEO';
import { Header, Footer } from './sections';
import { ShareButtons } from './ShareButtons';
import { RelatedPosts } from './RelatedPosts';
import { TableOfContents } from './TableOfContents';
import { ErrorBoundary } from './ErrorBoundary';
import { useLanguageSwitcher } from '../contexts/LanguageSwitcherContext';
import { getTranslationUrlsFromNode } from '../lib/polylangTranslations';
import { DEFAULT_BLOG_AUTHOR, CTA_DEFAULTS, CTA_DEFAULTS_FI } from '../constants/blog';
import { resolveAuthorShown, normalizeBlogAuthor, toFullAuthorName, stripHtml } from '../lib/blogUtils';
import { rewriteWpContentImageUrls } from '../lib/wpMedia';
import { buildHreflangTags } from '../lib/siteUrl';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const SITE_URL =
  (process.env.NEXT_PUBLIC_SITE_URL || 'https://identio.fi').replace(/\/$/, '');

/**
 * Blog post template: hero (ACF heroImage or featuredImage), author from ACF authorShown.
 * Byline: ACF blogPostEn.authorShown (string or array e.g. ["Enes Sehic"]); fallback when null: constants/blog.js
 */
export function BlogPostPage({ post, generalSettings, relatedPosts = [], lang = 'en' }) {
  const { setLanguageSwitcherUrls } = useLanguageSwitcher();
  const { title: siteTitle, description: siteDescription } = generalSettings ?? {};
  const postTitle = post?.title ?? '';
  const rawContent = post?.content ?? '';
  const content = useMemo(() => rewriteWpContentImageUrls(rawContent), [rawContent]);
  const blogPost = post?.blogPostEn ?? post?.blog_post ?? post?.blogPost ?? null;

  useLayoutEffect(() => {
    const urls = getTranslationUrlsFromNode(post, { basePathFi: '/blogi', basePathEn: '/blog' });
    if (urls) {
      setLanguageSwitcherUrls(urls);
    }
    return () => {
      setLanguageSwitcherUrls({});
    };
  }, [post?.databaseId, post?.language?.code, setLanguageSwitcherUrls]);
  const heroImageUrl =
    blogPost?.heroImage?.node?.sourceUrl ??
    post?.featuredImage?.node?.sourceUrl ??
    null;
  const colorTheme = blogPost?.blogPostColorTheme ?? blogPost?.blog_post_color_theme ?? null;
  // Byline: same source as list (authorShown → WP author → default), then full name for display.
  const raw = blogPost?.authorShown ?? blogPost?.author_shown;
  const resolved =
    normalizeBlogAuthor(resolveAuthorShown(raw)) ??
    normalizeBlogAuthor(post?.author?.node?.name) ??
    DEFAULT_BLOG_AUTHOR;
  const author = toFullAuthorName(resolved);

  // SEO: prefer WP `seoFields` (seoTitle, seoDescription); fallback to post title/excerpt.
  const seoTitleOverride = (post?.seoFields?.seoTitle ?? '').trim();
  const seoDescriptionOverride = (post?.seoFields?.seoDescription ?? '').trim();
  const excerptStripped = stripHtml(post?.excerpt ?? '');
  const defaultTitle = postTitle ? `${postTitle} | ${siteTitle}` : siteTitle;
  const seoTitle = seoTitleOverride || defaultTitle;
  const metaDescription = seoDescriptionOverride || excerptStripped || siteDescription || '';
  const basePath = lang === 'fi' ? '/blogi' : '/blog';
  const postSlug = post?.slug ?? '';
  const canonicalUrl = SITE_URL && postSlug ? `${SITE_URL}${basePath}/${postSlug}` : '';

  // Hreflang: build from Polylang translation data
  const translationUrls = getTranslationUrlsFromNode(post, { basePathFi: '/blogi', basePathEn: '/blog' });
  const hreflangTags = translationUrls
    ? buildHreflangTags(translationUrls.fiUrl, translationUrls.enUrl)
    : postSlug ? buildHreflangTags(`${basePath}/${postSlug}`, null) : undefined;

  // CTA (ACF Blog Post Settings) – use defaults when empty; language-specific defaults for Finnish
  const ctaDefaults = lang === 'fi' ? CTA_DEFAULTS_FI : CTA_DEFAULTS;
  const ctaTitle = (blogPost?.ctaTitle ?? blogPost?.cta_title ?? '').trim() || ctaDefaults.title;
  const ctaDescription = (blogPost?.ctaDescription ?? blogPost?.cta_description ?? '').trim() || ctaDefaults.description;
  const ctaButtonText = (blogPost?.ctaButtonText ?? blogPost?.cta_button_text ?? '').trim() || ctaDefaults.buttonText;
  const ctaButtonUrl = (blogPost?.ctaButtonUrl ?? blogPost?.cta_button_url ?? '').trim() || ctaDefaults.buttonUrl;
  const showCta = true;

  const articleJsonLd = postTitle
    ? {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: postTitle,
        ...(metaDescription && { description: metaDescription }),
        ...(heroImageUrl && { image: [heroImageUrl] }),
        ...(post?.date && { datePublished: post.date }),
        ...(post?.modified && { dateModified: post.modified }),
        ...(author && { author: { '@type': 'Person', name: author } }),
        ...(siteTitle && { publisher: { '@type': 'Organization', name: siteTitle } }),
        ...(canonicalUrl && { url: canonicalUrl }),
      }
    : null;

  return (
    <>
      <SEO
        title={seoTitle}
        description={metaDescription}
        imageUrl={heroImageUrl}
        url={canonicalUrl}
        type="article"
        publishedTime={post?.date || undefined}
        modifiedTime={post?.modified || undefined}
        alternates={hreflangTags}
      />
      {articleJsonLd && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
          />
        </Head>
      )}
      <div className="min-h-screen bg-white" style={colorTheme ? { ['--blog-accent']: colorTheme } : undefined}>
        <Header />
        <article>
          {heroImageUrl && (
            <section className="relative w-full bg-navy">
              <div className="max-w-5xl mx-auto px-6 py-8 md:py-12">
                <div className="aspect-[2/1] w-full relative overflow-hidden rounded-2xl bg-white/10">
                  <Image
                    src={heroImageUrl}
                    alt={postTitle || ''}
                    fill
                    sizes="(max-width: 1024px) 100vw, 1024px"
                    className="object-contain"
                  />
                </div>
              </div>
            </section>
          )}
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 md:py-16 xl:grid xl:grid-cols-[1fr_minmax(0,13rem)] xl:gap-12 min-w-0">
            <div className="min-w-0 max-w-3xl">
            <motion.header
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-10"
            >
              <Link
                href={lang === 'fi' ? '/blogi' : '/blog'}
                className="inline-flex items-center gap-2 text-brand font-medium text-sm mb-6 hover:gap-3 transition-all"
              >
                <ArrowLeft className="w-4 h-4 shrink-0" />
                {lang === 'fi' ? 'Ajatuksia' : 'Insights'}
              </Link>
              <h1 id="post-title" className="heading-1 text-gray-900 mb-4 scroll-mt-24">{postTitle}</h1>
              <div className="flex flex-wrap items-center justify-between gap-4 min-h-[3rem] border-t border-b border-gray-200 py-4 mb-6">
                {author && (
                  <p className="text-gray-600 font-medium m-0 flex items-center">
                    {lang === 'fi' ? `Kirjoittaja: ${author}` : `By ${author}`}
                  </p>
                )}
                <ShareButtons slug={post?.slug ?? ''} basePath={lang === 'fi' ? '/blogi' : '/blog'} lang={lang} className="flex items-center" />
              </div>
            </motion.header>
            <style dangerouslySetInnerHTML={{ __html: `
              [data-blog-content] .prose { font-size: 16px !important; line-height: 1.7 !important; }
              @media (min-width: 768px) {
                [data-blog-content] .prose { font-size: 18px !important; line-height: 1.8 !important; }
              }
              [data-blog-content] .prose ul { padding-left: 0.75rem !important; }
              [data-blog-content] .prose ul ul { padding-left: 0.25rem !important; }
              @media (min-width: 768px) {
                [data-blog-content] .prose ul { padding-left: 1.25rem !important; }
                [data-blog-content] .prose ul ul { padding-left: 0.5rem !important; }
              }
              [data-blog-content] .prose ul ul li::before { display: none !important; }
              [data-blog-content] .prose ul ul { list-style-type: circle !important; list-style-position: outside !important; padding-left: 1.25em !important; }
              [data-blog-content] .prose ul ul li { padding-left: 0.35em !important; }
              [data-blog-content] .prose ul ul li::marker { color: #051ab7 !important; }
            ` }} />
            {/* Blog content from WordPress – trusted source (docs/SECURITY.md) */}
            <ErrorBoundary>
              <motion.div
                data-blog-content
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="blog-content prose prose-lg max-w-none
                  [&_[id]]:scroll-mt-24
                  prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-gray-900
                  prose-h2:mt-12 prose-h2:mb-4 prose-h2:text-2xl prose-h2:md:text-3xl prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2
                  prose-h3:mt-8 prose-h3:mb-3 prose-h3:text-xl prose-h3:md:text-2xl
                  prose-h4:mt-6 prose-h4:mb-2 prose-h4:text-lg prose-h4:md:text-xl
                  prose-p:text-gray-600 prose-p:leading-relaxed
                  prose-a:text-brand prose-a:no-underline hover:prose-a:underline prose-a:font-medium
                  prose-strong:text-gray-900 prose-strong:font-semibold
                  prose-ul:my-6 prose-ul:list-none prose-ul:pl-3 md:prose-ul:pl-5 prose-ul:text-gray-600 prose-ul:space-y-2 [&_ul_ul]:pl-1 md:[&_ul_ul]:pl-2
                  prose-ol:my-6 prose-ol:list-decimal prose-ol:pl-6 prose-ol:text-gray-600 prose-ol:space-y-2
                  prose-li:my-1 prose-li:leading-relaxed
                  prose-blockquote:my-6
                  [&_cite]:text-[15px]
                  [&_img]:rounded-xl [&_img]:shadow-md
                  after:content-[''] after:table after:clear-both"
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </ErrorBoundary>

            <RelatedPosts
              manualOverride={blogPost?.relatedPostsOverride ?? blogPost?.related_posts_override}
              relatedPosts={relatedPosts}
              currentSlug={post?.slug}
              basePath={lang === 'fi' ? '/blogi' : '/blog'}
              lang={lang}
            />
            </div>
            <aside className="hidden xl:block" aria-label="Table of contents">
              <TableOfContents title={postTitle} lang={lang} />
            </aside>
          </div>

          {showCta && (
            <motion.section
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className="w-full relative overflow-hidden bg-navy text-white"
            >
              <div className="absolute inset-0 opacity-5">
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
              </div>
              <div className="max-w-4xl mx-auto px-6 py-20 md:py-32 text-center relative z-10">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5">
                  {ctaTitle}
                </h2>
                <p className="text-lg md:text-xl text-gray-300 mb-10 leading-relaxed max-w-3xl mx-auto">
                  {ctaDescription}
                </p>
                {ctaButtonUrl && (ctaButtonUrl.startsWith('/') || (SITE_URL && ctaButtonUrl.startsWith(SITE_URL))) ? (
                  <Link
                    href={ctaButtonUrl.startsWith('/') ? ctaButtonUrl : ctaButtonUrl.slice(SITE_URL.length) || '/'}
                    className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 shadow-2xl text-lg px-10 py-5 rounded-xl font-medium transition-all duration-200"
                  >
                    {ctaButtonText}
                    <ArrowRight className="w-5 h-5 shrink-0" />
                  </Link>
                ) : (
                  <a
                    href={ctaButtonUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-white hover:bg-gray-100 shadow-2xl text-lg px-10 py-5 rounded-xl font-medium transition-all duration-200"
                  >
                    {ctaButtonText}
                    <ArrowRight className="w-5 h-5 shrink-0" />
                  </a>
                )}
              </div>
            </motion.section>
          )}
        </article>
        <Footer />
      </div>
    </>
  );
}

BlogPostPage.propTypes = {
  post: PropTypes.shape({
    title: PropTypes.string,
    content: PropTypes.string,
    slug: PropTypes.string,
    featuredImage: PropTypes.object,
    seoFields: PropTypes.shape({
      seoTitle: PropTypes.string,
      seoDescription: PropTypes.string,
    }),
    blogPostEn: PropTypes.object,
    blog_post: PropTypes.object,
    blogPost: PropTypes.object,
  }),
  generalSettings: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
  }),
  relatedPosts: PropTypes.arrayOf(PropTypes.object),
  lang: PropTypes.oneOf(['en', 'fi']),
};
